import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import Entity from '../../Common/Entity';
import { getpaginateDPAction } from './API/Action';
import CustomChip from '../../Components/CustomChip';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import AttachedDPList from '../../PO/Components/AttachedDPList';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';
import { TextField } from '../../../v2/MUI_custom_component';
import { SearchOutlined } from '../../../components/icons';
import { MODULE_PERMISSION_ENUM } from '../../Sidebar/Enum';
import NoAccess from '../../Common/NoAccess/NoAccess';

const PAGE_SIZE = 10;

const DPList = () => {
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [dateFilter, setDateFilter] = useState('');
    const [searchOrderId, setSearchOrderId] = useState('');

    const { selectedSupplierData, selectedEntity: selectedCompanyEntity, selectedEntityInfo } = useSelector(
        (state) => state.entityReducer
    );
    const hasAccess = selectedEntityInfo?.has_access;

    const statusOptions = ['pending', 'approved', 'partially_delivered', 'delivered', 'cancelled'];
    const statusLabels = {
        pending: 'Pending',
        approved: 'Approved',
        partially_delivered: 'Partially Delivered', 
        delivered: 'Delivered',
        cancelled: 'Cancelled'
    };
    
    const dateFilters = [
        { value: '1month', label: 'Past 1 month' },
        { value: '3months', label: 'Past 3 months' },
    ];
    
    const getStartEndDate = useCallback(() => {
        const now = dayjs();
        switch (dateFilter) {
        case '1month':
            return {
            startDate: now.subtract(1, 'month').toDate(),
            endDate: now.toDate()
            };
        case '3months':
            return {
            startDate: now.subtract(3, 'month').toDate(),
            endDate: now.toDate()
            };
        default:
            return { startDate: null, endDate: null };
        }
    }, [dateFilter]);

    const fetchDPList = useCallback(async (page, pageSize) => {
        const config = new URLSearchParams();
        config.append('f', 'id$gte$0');
        config.append('o', 'id$desc');
        config.append('page', page.toString());
        config.append('page_size', pageSize.toString());
        config.append('is_buyer', 'true');
    
        if (selectedCompanyEntity) {
            config.append('f', `buyer_esi_id$eq$${selectedCompanyEntity}`);
        }
        
        if (selectedStatus.length > 0) {
            config.append('f', `final_status$in$${selectedStatus.join('~')}`);
        }

        if (searchOrderId.trim()) {
            config.append('f', `id$eq$${searchOrderId.trim()}`);
        }
    
        const { startDate, endDate } = getStartEndDate();
        if (startDate && endDate) {
            config.append('f', `date$gte$${dayjs(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ssZ')}`);
            config.append('f', `date$lte$${dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ssZ')}`);
        }
        
        try {
            const result = await getpaginateDPAction(config);
            if (result.error) {
                toast.error(result.message || 'Failed to fetch dispatch plans');
            }
            return result;
        } catch (error) {
            toast.error('Failed to fetch dispatch plans');
            return { error: true, data: [] };
        }
    }, [selectedCompanyEntity, selectedStatus, getStartEndDate, searchOrderId]);
    
    const {
        data: dpList,
        loading,
    } = useInfiniteScroll({
        fetchFunction: fetchDPList,
        pageSize: PAGE_SIZE,
        dependencies: (selectedCompanyEntity && hasAccess !== false) ? [selectedCompanyEntity, selectedStatus, dateFilter, searchOrderId] : []
    });

    const handleStatusFilter = (status) => {
        const newSelectedStatus = selectedStatus.includes(status)
        ? selectedStatus.filter(s => s !== status)
        : [...selectedStatus, status];
        setSelectedStatus(newSelectedStatus);
    };
    const isEntityEnabled = selectedSupplierData?.entity_enabled;
    return (
        <>
        {isEntityEnabled &&  (
            <Box sx={{ 
                display: 'flex', 
                justifyContent:'end', 
                p: { xs: 2, md: 0 },
                mr: { xs: 0, md: 0 }, 
                ml: {xs: 0, md: 2 },
                pb: { xs: 0, md: 0 },
                position: {xs: 'fixed', md: 'relative'}, 
                top: {xs: '88px', md: 0}, 
                left: {xs: 0, md: 'auto'},
                right: {xs: 0, md: 'auto'},
                zIndex: {xs: 1000, md: 'auto'}, 
                boxShadow: {xs: '0px 2px 8px 0px #0000001A', md: 'none'},
                background: {xs: '#FAFAFA', md: 'transparent'},
                gap: 2,
            }}>
                <Entity
                    module_name={MODULE_PERMISSION_ENUM.DISPATCH_PLAN}
                    desktopmb = {0}
                    entityLabel="SAP Code*"
                    showEntitySelect={true}
                    forceRefresh={false}
                    gridSizes={{
                        entity: { xs: 12, sm: 6, md:12 }
                    }}
                />
                {hasAccess !== false && (
                    <TextField
                        size="small"
                        placeholder="Search by Order ID"
                        label="Order ID"
                        value={searchOrderId}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setSearchOrderId(value);
                        }}
                        sx={{ 
                            display: {xs: 'none', md: 'flex'},
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchOutlined width={20} height={20} />
                                    </InputAdornment>
                                )
                            },
                        }}
                    />  
                )}
            </Box>
        )}
        {hasAccess === false ?(
                <Box sx={{
                    display: 'flex',
                    justifyContent:'center',
                    alignItems: 'center',
                    height: '60vh',
                }}>
                    <NoAccess />
                </Box>
                
            )
            : (
                <>
                <TextField
                    size="small"
                    placeholder="Search by Order ID"
                    label="Order ID"
                    value={searchOrderId}
                    onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setSearchOrderId(value);
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchOutlined width={20} height={20} />
                                </InputAdornment>
                            )
                        },
                    }}
                    sx={{ 
                        mt: {xs : isEntityEnabled ? '96px': 0, md: 0 },
                        display: {xs: 'flex', md: 'none'},
                        px: 2,
                    }}
                />  
                <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    overflowX: 'scroll', 
                    pt: { xs: 2, md: 2 },
                    pb: { xs: '2px', md: 2 },
                    px: 2,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}>
                    {[
                        ...statusOptions.map((status) => ({
                            key: status,
                            label: statusLabels[status],
                            selected: selectedStatus.includes(status),
                            onClick: () => handleStatusFilter(status),
                            onDelete: selectedStatus.includes(status) ? () => handleStatusFilter(status) : undefined,
                        })),
                        ...dateFilters.map((filter) => ({
                            key: filter.value,
                            label: filter.label,
                            selected: dateFilter === filter.value,
                            onClick: () => setDateFilter(filter.value),
                            onDelete: dateFilter === filter.value ? () => setDateFilter('') : undefined,
                        }))
                    ]
                        .sort((a, b) => b.selected - a.selected) // Selected chips first
                        .map((chip) => (
                            <CustomChip
                                key={chip.key}
                                label={chip.label}
                                onClick={chip.onClick}
                                onDelete={chip.onDelete}
                                selected={chip.selected}
                                variant={chip.selected ? "filled" : "outlined"}
                            />
                        ))
                    }
                </Box>
                <Box sx={{ p: { xs: 2, md: 2 }, pt: { xs: 2, md: 0 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box 
                        sx={{ 
                            display: 'flex',
                            gap: 2,
                            flexDirection: 'column',
                        }}
                    >
                        
                        <AttachedDPList
                            attachedDPData={dpList}
                            loading={loading} 
                            isViewAttachedDPData={false}
                        />
                    </Box>
                </Box>
                </>
            )}
        </>
    )
}

export default DPList